import * as Sentry from "@sentry/react"
import {
  useEffect,
  useRef,
  type FC,
  type PropsWithChildren,
  type ReactNode,
} from "react"
import { Button } from "./ui"
import { ExclamationCircleIcon } from "@heroicons/react/24/outline"
import { useRouterState } from "@tanstack/react-router"

type Props = {
  children: ReactNode
  ScreenWrapper: FC<PropsWithChildren>
  navigateBack: () => void
  navigateHome: () => void
}

function ErrorFallback({
  ScreenWrapper,
  resetError,
  navigateHome,
  navigateBack,
}: {
  resetError: () => void
} & Omit<Props, "children">) {
  const routerState = useRouterState()
  const routerStateRef = useRef(routerState)
  useEffect(() => {
    if (
      routerStateRef.current.location.pathname !==
        routerState.location.pathname &&
      !routerState.isLoading &&
      !routerState.isTransitioning
    ) {
      resetError()
    }
  }, [
    routerState.location.pathname,
    resetError,
    routerState.isLoading,
    routerState.isTransitioning,
  ])

  return (
    <ScreenWrapper>
      <div className="calculated-height flex flex-col items-center justify-center">
        <div className="max-w-2/6 flex flex-col items-center gap-4">
          <ExclamationCircleIcon className="size-60 text-light-grey" />
          <h1 className="!font-mulish text-xl font-bold">
            Oeps! Er is iets misgegaan.
          </h1>
          <p className="text-center">
            De pagina kon niet worden geladen.
            <br />
            Probeer het later opnieuw.
          </p>
          <div>
            <Button
              onClick={() => {
                navigateHome()
              }}
            >
              Naar startpagina
            </Button>
          </div>

          <div>
            <Button
              variant={"secondary"}
              onClick={() => {
                navigateBack()
              }}
            >
              Terug
            </Button>
          </div>
        </div>
      </div>
    </ScreenWrapper>
  )
}

export function ErrorBoundary({ children, ScreenWrapper, ...props }: Props) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <ErrorFallback
          ScreenWrapper={ScreenWrapper}
          resetError={resetError}
          {...props}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
